// CHAT-DE: what the "isn't in #channel. Add them?" prompt needs to remember, per tab and viewer.
// - The messages this tab sent (their `client_id`s): the prompt shows under a message only right
//   after its author sent it here, never on old messages or someone else's (Slack's ephemeral
//   "only visible to you").
// - The prompts answered this session ("Just this once", or after "Add").
// - "No": the DEs this viewer asked never to be prompted about again in a channel (this browser
//   only; storage may be off, then it lasts the session).
import { useSyncExternalStore } from "react";

const sent = new Set<string>();
const answered = new Set<string>();
const declinedHere = new Set<string>();
const listeners = new Set<() => void>();
let version = 0;

const bump = () => {
  version += 1;
  for (const l of listeners) l();
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

const DECLINED_KEY = "cos.chat.de-prompt.declined";

function readDeclined(): Set<string> {
  try {
    const raw = window.localStorage.getItem(DECLINED_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(list) ? list.filter((x) => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

/** Notes a message this tab just sent (its `client_id`). */
export function noteSent(clientId: string): void {
  sent.add(clientId);
  bump();
}

/** Whether this tab sent the message with this `client_id`. */
export const sentFromHere = (clientId: string | null | undefined): boolean =>
  Boolean(clientId && sent.has(clientId));

/** The prompt of one message's DE was answered ("Just this once", or added). */
export function answerPrompt(clientId: string, deId: string): void {
  answered.add(`${clientId}|${deId}`);
  bump();
}

/** Who is asking: the company and the viewer's membership (another account never inherits). */
export const viewerScope = (co: string, meId: string | null) => `${co}|${meId ?? "-"}`;

/** "No": never prompt about this DE in this channel again (this viewer, this browser). */
export function declineInChannel(scope: string, channelId: string, deId: string): void {
  const key = `${scope}|${channelId}|${deId}`;
  declinedHere.add(key);
  try {
    const all = readDeclined();
    all.add(key);
    window.localStorage.setItem(DECLINED_KEY, JSON.stringify([...all].slice(-200)));
  } catch {}
  bump();
}

/** Whether a message's prompt about `deId` in `channelId` still shows. */
export function promptOpen(
  clientId: string | null | undefined,
  scope: string,
  channelId: string,
  deId: string,
): boolean {
  if (!clientId || !sent.has(clientId)) return false;
  if (answered.has(`${clientId}|${deId}`)) return false;
  const key = `${scope}|${channelId}|${deId}`;
  return !declinedHere.has(key) && !readDeclined().has(key);
}

/** Re-renders when any of the above changes. */
export function useAskedHere(): number {
  return useSyncExternalStore(
    subscribe,
    () => version,
    () => version,
  );
}

/** Tests: forget everything. */
export function resetAskedHere(): void {
  sent.clear();
  answered.clear();
  declinedHere.clear();
  bump();
}
